// Anke, 2026-07-16 — per-paragraph alignment reader for the LIVE edit session.
//
// The options circle already WROTE alignment per paragraph while editing
// (`textbox/apply-align` → `setTextAlign` at the caret), but read it back from
// the whole-box `obj.hAlign`. Write and read disagreed: aligning one paragraph
// moved the text while the circle kept showing the box default, and moving the
// caret between differently aligned paragraphs never changed the highlight.
// This reader closes that gap — the caret's block now drives the highlight, the
// same way `#readSelectionListBlock` already does for the list buttons.
//
// PURE and READ-ONLY, mirroring `readSelectionMarkState`: it touches only
// `state.doc` and `state.selection`, never `chain()`, `setMark` or `.focus()`,
// so refreshing the highlight can neither mutate the document nor steal focus.
//
// Alignment is a BLOCK attribute, so it is not part of `SelectionMarkSnapshot`
// (inline marks only) — it rides the bus as its own `alignBlock` field, exactly
// like `listBlock`. It reuses `SelectionMarkState` because the classification is
// identical: uniform → on, blocks disagree → mixed.

import type { EditorState } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { SelectionMarkState } from './selection-mark-state.type.js';

/**
 * The alignment a block actually RENDERS with: its own attribute when the user
 * set one, otherwise the textbox-wide default.
 *
 * An unaligned block is attribute-less by design (`defaultAlignment: null` in
 * `tiptap-extensions.ts`) so it INHERITS the wrapper default that
 * `buildTextboxHtmlElement` writes from `obj.hAlign`. Substituting the fallback
 * here is what makes the circle show what is on screen rather than "nothing".
 * With no fallback either (legacy JSON carries no `hAlign`) the block reports
 * `null` — fabricating a value would light a button the text does not follow.
 */
function alignOf(node: ProseMirrorNode, fallback: string | undefined): string | null {
    const own = node.attrs?.['textAlign'];
    const effective = own ?? fallback;
    return effective === null || effective === undefined || effective === '' ? null : String(effective);
}

/**
 * Read the alignment at the current selection.
 *
 * - COLLAPSED caret → the enclosing block, never `mixed` (a point sits in one
 *   block). Read via `$from.parent` rather than a `nodesBetween` walk, which is
 *   defined in terms of a range and is needless indirection for a single point.
 * - RANGE → every text block it touches; they disagree → `mixed`, carrying the
 *   distinct values present.
 */
export function readSelectionAlignState(
    state: EditorState,
    fallback: string | undefined,
): SelectionMarkState {
    const values: (string | null)[] = [];
    if (state.selection.empty) {
        values.push(alignOf(state.selection.$from.parent, fallback));
    } else {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node) => {
            if (node.isTextblock) values.push(alignOf(node, fallback));
        });
    }

    const present = [...new Set(values.filter((v): v is string => v !== null))];
    const hasOff = values.some((v) => v === null);
    if (present.length === 0) return { state: 'off' };
    if (present.length === 1 && !hasOff) return { state: 'on', value: present[0]! };
    return { state: 'mixed', present };
}
